import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { companyService, type Company } from '../../services'
import { Link } from 'react-router-dom'
import { useState, useCallback } from 'react'
import { useLocale } from '../../hooks/use-locale'
import PageLayout from '../../components/layout/page-layout'
import { Container } from '../../components/ui/container'
import { PageHeader } from '../../components/layout/page-header'
import { PagePanel } from '../../components/layout/page-panel'
import { pageInput } from '../../components/layout/page-styles'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import Table from '../../components/ui/table'
import { StatCard } from '../../components/dashboard/stat-card'
import { Building2, CheckCircle2, Clock, Plus, Search } from 'lucide-react'

const SALES_STATUS_KEY_MAP: Record<string, string> = {
  proposal_sent: 'proposalSent',
  contract_signed: 'contractSigned',
  active_customer: 'activeCustomer',
}

function getSalesStatusVariant(status: string): 'default' | 'success' | 'warning' | 'error' {
  const variants: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
    lead: 'default',
    qualified: 'default',
    proposal_sent: 'warning',
    negotiation: 'warning',
    contract_signed: 'success',
    active_customer: 'success',
    lost: 'error',
  }
  return variants[status] || 'default'
}

export default function CompaniesOverview() {
  const { t } = useTranslation()
  const { formatDate } = useLocale()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')

  const getSalesStatusLabel = useCallback(
    (status: string) => {
      const key = SALES_STATUS_KEY_MAP[status] || status
      return t(`crm.salesStatusValues.${key}`)
    },
    [t]
  )

  const getInterestLevelLabel = useCallback(
    (level: string) => t(`crm.interestLevelValues.${level}`),
    [t]
  )

  const approveMutation = useMutation({
    mutationFn: (args: { id: string; isApproved: boolean }) =>
      companyService.updateApproval(args.id, { isApproved: args.isApproved }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['companies', page, search],
    queryFn: () =>
      companyService.getCompanies({
        page,
        limit: 10,
        search: search || undefined,
      }),
  })

  const companies: (Company & { isApproved?: boolean })[] = (data?.companies || []) as any
  const total = data?.total || companies.length
  const currentPage = data?.page || page
  const limit = data?.limit || 10
  const activeCount = companies.filter(c => c.isActive).length
  const pendingApprovalCount = companies.filter(c => !c.isApproved).length

  return (
    <PageLayout
      isLoading={isLoading}
      error={error}
      loadingMessage={t('companies.loading')}
      errorMessage={t('companies.loadError')}
    >
      <Container>
        <PageHeader
          title={t('companies.manageTitle')}
          subtitle={t('companies.subtitle')}
          action={
            <Link to='/companies/new'>
              <Button variant='primary' size='md'>
                <Plus className='w-4 h-4 mr-2' />
                {t('companies.newCompany')}
              </Button>
            </Link>
          }
        />

        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <StatCard
            icon={Building2}
            label={t('companies.companiesCount')}
            value={total}
          />
          <StatCard
            icon={CheckCircle2}
            label={t('companies.activeCompanies')}
            value={activeCount}
            iconClassName='text-success-700'
            iconBgClassName='bg-success-50 border border-success-100'
          />
          <StatCard
            icon={Clock}
            label={t('companies.pendingApproval')}
            value={pendingApprovalCount}
            iconClassName='text-warning-700'
            iconBgClassName='bg-warning-50 border border-warning-100'
          />
        </div>

        <PagePanel padding>
          <div className='relative max-w-sm'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400' />
            <input
              type='text'
              placeholder={t('companies.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={pageInput}
            />
          </div>
        </PagePanel>

        <Table
          dense
          data={companies}
          rowKey={company => company.id}
          emptyMessage={t('companies.noCompanies')}
          columns={[
            {
              header: t('companies.companyName'),
              cell: company => (
                <div className='min-w-0'>
                  <div className='font-medium text-neutral-900'>{company.name}</div>
                  {company.description ? (
                    <div className='text-xs text-neutral-500 mt-0.5 truncate'>{company.description}</div>
                  ) : null}
                </div>
              ),
            },
            {
              header: t('common.status'),
              cell: company => (
                <div className='flex flex-wrap items-center gap-2'>
                  <Badge
                    title={company.isActive ? t('common.active') : t('common.inactive')}
                    variant={company.isActive ? 'success' : 'error'}
                  />
                  <Badge
                    title={company.isApproved ? t('common.approved') : t('common.notApproved')}
                    variant={company.isApproved ? 'success' : 'warning'}
                  />
                  {company.salesStatus ? (
                    <Badge
                      title={getSalesStatusLabel(company.salesStatus)}
                      variant={getSalesStatusVariant(company.salesStatus)}
                    />
                  ) : null}
                </div>
              ),
            },
            {
              header: t('crm.interestLevel'),
              cell: company =>
                company.interestLevel ? (
                  <span className='text-xs px-2 py-1 bg-primary-50 text-primary-700 border border-primary-100 rounded-full'>
                    {getInterestLevelLabel(company.interestLevel)}
                  </span>
                ) : (
                  <span className='text-xs text-neutral-500'>{t('common.dash')}</span>
                ),
            },
            {
              header: t('crm.contactPersonName'),
              cell: company =>
                company.contactPersonName ? (
                  <div>
                    <div className='text-sm text-neutral-900'>{company.contactPersonName}</div>
                    {company.contactPersonEmail ? (
                      <div className='text-xs text-neutral-500'>{company.contactPersonEmail}</div>
                    ) : null}
                  </div>
                ) : (
                  <span className='text-xs text-neutral-500'>{t('common.dash')}</span>
                ),
            },
            {
              header: t('common.created'),
              className: 'whitespace-nowrap',
              cell: company =>
                company.createdAt ? formatDate(company.createdAt) : t('common.dash'),
            },
            {
              header: t('common.actions'),
              className: 'text-right whitespace-nowrap',
              cell: company => (
                <div className='flex items-center justify-end gap-2'>
                  {!company.isApproved ? (
                    <button
                      onClick={() =>
                        approveMutation.mutate({ id: company.id, isApproved: true })
                      }
                      className='px-3 py-1 bg-warning-50 text-warning-700 border border-warning-100 hover:bg-warning-100 rounded-full text-xs font-medium transition-colors duration-150 disabled:opacity-50'
                      disabled={approveMutation.isPending}
                    >
                      {t('companies.approve')}
                    </button>
                  ) : null}
                  <Link
                    to={`/companies/${company.id}/crm`}
                    className='px-3 py-1 bg-primary-50 text-primary-700 border border-primary-100 hover:bg-primary-100 rounded-full text-xs font-medium transition-colors duration-150'
                  >
                    {t('companies.crmLink')}
                  </Link>
                  <Link
                    to={`/companies/${company.id}/edit`}
                    className='px-3 py-1 bg-neutral-50 text-neutral-700 border border-neutral-200 hover:bg-neutral-100 rounded-full text-xs font-medium transition-colors duration-150'
                  >
                    {t('common.edit')}
                  </Link>
                  <Link
                    to={`/companies/${company.id}/users`}
                    className='px-3 py-1 bg-success-50 text-success-700 border border-success-100 hover:bg-success-50 rounded-full text-xs font-medium transition-colors duration-150'
                  >
                    {t('companies.usersLink')}
                  </Link>
                </div>
              ),
            },
          ]}
        />

        {total > limit ? (
          <div className='flex items-center justify-center gap-2'>
            <Button
              variant='secondary'
              size='sm'
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              {t('common.back')}
            </Button>
            <span className='px-2 text-sm text-neutral-500'>
              {t('common.pageOf', {
                page: currentPage,
                totalPages: Math.ceil(total / limit),
                total,
              })}
            </span>
            <Button
              variant='secondary'
              size='sm'
              onClick={() => setPage(p => p + 1)}
              disabled={currentPage * limit >= total}
            >
              {t('common.next')}
            </Button>
          </div>
        ) : null}
      </Container>
    </PageLayout>
  )
}
